import { DEFAULT_SMART_LIST_ADAPTER_ID, DEFAULT_SMART_LIST_SORT_OPTION } from "../../constants";

/**
 * Themes for the website structure
 * @enum {string}
 * @readonly
 * @property {string} ENTERTAINMENT - Entertainment theme
 * @property {string} NEWS - News theme
 * @property {string} SPORTS - Sports theme
 * @property {string} CUSTOM - Custom theme
 */
export type WebsiteStructureTheme = "ENTERTAINMENT" | "NEWS" | "SPORTS" | "CUSTOM";

/**
 * Template IDs for smart lists
 * @enum {number}
 * @readonly
 * @property {number} GENRE - Genre smart list template ID
 * @property {number} LABELS - Labels smart list template ID
 * @property {number} PROMO - Promo smart list template ID
 * @property {number} SPORTS - Sports smart list template ID
 * @property {number} CUSTOM - Custom smart list template ID
 */
const SMART_LIST_TEMPLATE_IDS = {
    GENRE: 107,
    LABELS: 109,
    PROMO: 112,
    SPORTS: 137,
} as const;

/**
 * Definition for a smart list
 * @typedef {Object} SmartListDefinition
 * @property {string} title - Title of the smart list
 * @property {string} [genre] - Genre of the smart list
 * @property {string} [labels] - Labels of the smart list
 * @property {number} templateId - Template ID of the smart list
 * @property {number} adapterId - Adapter ID of the smart list
 * @property {string} [sortOption] - Sort option of the smart list
 */
export type SmartListDefinition = {
    title: string;
    genre?: string;
    labels?: string;
    templateId: number;
    adapterId: number;
    sortOption?: string;
};

/**
 * Definition for a carousel
 * @typedef {Object} CarouselDefinition
 * @property {string} title - Title of the carousel
 * @property {string} [description] - Description of the carousel
 */
export type CarouselDefinition = {
    title: string;
    description?: string;
};

/**
 * Definition for a theme page
 * @typedef {Object} ThemePageDefinition
 * @property {string} title - Title of the page
 * @property {string} slug - Slug of the page
 */
export type ThemePageDefinition = {
    title: string;
    slug: string;
};

/**
 * Definition for a theme menu item
 * @typedef {Object} ThemeMenuItemDefinition
 * @property {string} title - Title of the menu item
 * @property {string} url - URL of the menu item
 */
export type ThemeMenuItemDefinition = {
    title: string;
    url: string;
};
/**
 * Definition for a theme menu
 * @typedef {Object} ThemeMenuDefinition
 * @property {"Header" | "Footer"} layout - Layout of the menu
 * @property {ThemeMenuItemDefinition[]} items - Items of the menu
 */
export type ThemeMenuDefinition = {
    layout: "Header" | "Footer";
    items: ThemeMenuItemDefinition[];
};

/**
 * Definition for a website theme
 * @typedef {Object} WebsiteThemeDefinition
 * @property {WebsiteStructureTheme} id - ID of the theme
 * @property {string} name - Name of the theme
 * @property {string} description - Description of the theme
 * @property {ThemePageDefinition[]} pages - Pages of the theme
 * @property {Record<string, SmartListDefinition[]>} [smartLists] - Smart lists of the theme
 * @property {Record<string, CarouselDefinition[]>} [carousels] - Carousels of the theme
 * @property {ThemeMenuDefinition[]} [menus] - Menus of the theme
 * @property {string} [logoTargetUrl] - Target URL for the logo
 */
export type WebsiteThemeDefinition = {
    id: WebsiteStructureTheme;
    name: string;
    description: string;
    pages: ThemePageDefinition[];
    smartLists?: Record<string, SmartListDefinition[]>;
    carousels?: Record<string, CarouselDefinition[]>;
    menus?: ThemeMenuDefinition[];
    logoTargetUrl?: string;
};

/**
 * Definitions for the website themes
 * @type {Record<WebsiteStructureTheme, WebsiteThemeDefinition>}
 * @readonly
 * @property {WebsiteThemeDefinition} NEWS - News theme definition
 * @property {WebsiteThemeDefinition} ENTERTAINMENT - Entertainment theme definition
 * @property {WebsiteThemeDefinition} SPORTS - Sports theme definition
 * @property {WebsiteThemeDefinition} CUSTOM - Custom theme definition
 */
const THEME_DEFINITIONS: Record<WebsiteStructureTheme, WebsiteThemeDefinition> = {
    NEWS: {
        id: "NEWS",
        name: "News",
        description: "Clean, professional layout for news content",
        pages: [
            { title: "Home Page", slug: "/" },
            { title: "Top Stories", slug: "/top-stories" },
            { title: "World", slug: "/world" },
            { title: "Local", slug: "/local" },
        ],
        smartLists: {
            "/": [
                {
                    title: "Breaking News",
                    labels: "news",
                    templateId: SMART_LIST_TEMPLATE_IDS.LABELS,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
                {
                    title: "Top Headlines",
                    labels: "headlines",
                    templateId: SMART_LIST_TEMPLATE_IDS.LABELS,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
            ],
        },
        carousels: {
            "/": [
                {
                    title: "Featured Stories",
                    description: "Top news stories",
                },
            ],
        },
        menus: [
            {
                layout: "Header",
                items: [
                    { title: "Home Page", url: "/" },
                    { title: "Top Stories", url: "/top-stories" },
                    { title: "World", url: "/world" },
                    { title: "Local", url: "/local" },
                ],
            },
        ],
        logoTargetUrl: "/",
    },
    ENTERTAINMENT: {
        id: "ENTERTAINMENT",
        name: "Entertainment",
        description: "Engaging design for entertainment",
        pages: [
            { title: "Home Page", slug: "/" },
            { title: "Browse", slug: "/browse" },
        ],
        smartLists: {
            "/browse": [
                {
                    title: "Action",
                    genre: "action",
                    templateId: SMART_LIST_TEMPLATE_IDS.GENRE,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
            ],
            "/": [
                {
                    title: "Comedy",
                    genre: "comedy",
                    templateId: SMART_LIST_TEMPLATE_IDS.GENRE,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
                {
                    title: "Action",
                    genre: "action",
                    templateId: SMART_LIST_TEMPLATE_IDS.GENRE,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
                {
                    title: "Drama",
                    genre: "drama",
                    templateId: SMART_LIST_TEMPLATE_IDS.GENRE,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
            ],
        },
        carousels: {
            "/": [
                {
                    title: "Featured Content",
                    description: "Must-watch shows and movies",
                },
            ],
        },
        menus: [
            {
                layout: "Header",
                items: [
                    { title: "Home Page", url: "/" },
                    { title: "Browse", url: "/browse" },
                ],
            },
        ],
        logoTargetUrl: "/",
    },
    SPORTS: {
        id: "SPORTS",
        name: "Sports",
        description: "Dynamic, energetic layout for sports content",
        pages: [
            { title: "Home Page", slug: "/" },
            { title: "Live Scores", slug: "/scores" },
            { title: "Teams", slug: "/teams" },
            { title: "Highlights", slug: "/highlights" },
        ],
        smartLists: {
            "/": [
                {
                    title: "Live Games",
                    labels: "live,sports",
                    templateId: SMART_LIST_TEMPLATE_IDS.SPORTS,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
                {
                    title: "Top Highlights",
                    labels: "highlights,top",
                    templateId: SMART_LIST_TEMPLATE_IDS.SPORTS,
                    adapterId: DEFAULT_SMART_LIST_ADAPTER_ID,
                    sortOption: DEFAULT_SMART_LIST_SORT_OPTION,
                },
            ],
        },
        carousels: {
            "/": [
                {
                    title: "Featured Matches",
                    description: "Don't miss these games",
                },
            ],
        },
        menus: [
            {
                layout: "Header",
                items: [
                    { title: "Home Page", url: "/" },
                    { title: "Live Scores", url: "/scores" },
                    { title: "Teams", url: "/teams" },
                    { title: "Highlights", url: "/highlights" },
                ],
            },
        ],
        logoTargetUrl: "/",
    },
    CUSTOM: {
        id: "CUSTOM",
        name: "Custom",
        description: "Empty app with only header and footer",
        pages: [],
    },
};

const ORDERED_THEME_IDS: WebsiteStructureTheme[] = ["NEWS", "ENTERTAINMENT", "SPORTS", "CUSTOM"];

/**
 * Library for website themes
 * @class
 * @readonly
 * @property {WebsiteThemeDefinition} NEWS - News theme definition
 * @property {WebsiteThemeDefinition} ENTERTAINMENT - Entertainment theme definition
 * @property {WebsiteThemeDefinition} SPORTS - Sports theme definition
 * @property {WebsiteThemeDefinition} CUSTOM - Custom theme definition
 * @property {WebsiteThemeDefinition[]} list - List of all theme definitions
 * @property {WebsiteThemeDefinition} defaultTheme - Default theme definition
 * @property {WebsiteThemeDefinition} get - Get a theme definition by ID
 * @property {WebsiteThemeDefinition[]} list - List of all theme definitions
 */
export class WebsiteThemeLibrary {
    static get(theme: WebsiteStructureTheme): WebsiteThemeDefinition {
        return THEME_DEFINITIONS[theme];
    }

    static list(): WebsiteThemeDefinition[] {
        return ORDERED_THEME_IDS.map((theme) => THEME_DEFINITIONS[theme]);
    }

    static defaultTheme(): WebsiteStructureTheme {
        return "ENTERTAINMENT";
    }
}
